import { logger } from '@/utils/logger';

/**
 * UUID v7 utilities for extracting timestamps from time-ordered UUIDs
 *
 * UUID v7 structure (RFC 9562):
 * - unix_ts_ms (48 bits): Unix timestamp in milliseconds
 * - ver (4 bits): Version = 7
 * - rand_a (12 bits): Random data
 * - var (2 bits): Variant = 0b10
 * - rand_b (62 bits): Random data
 */

/**
 * Extract timestamp from a UUID v7 string
 * Returns null if the UUID is not v7 or invalid
 *
 * @param uuidStr - UUID string (with or without hyphens)
 * @returns Date object or null
 */
export function extractTimeFromUUIDv7(uuidStr: string): Date | null {
  try {
    // Remove hyphens for easier parsing
    const hex = uuidStr.replace(/-/g, '');

    // Validate length
    if (hex.length !== 32) {
      return null;
    }

    // Check version (byte 6, upper 4 bits should be 0111 = 7)
    const versionByte = parseInt(hex.substring(12, 14), 16);
    const version = (versionByte >> 4) & 0x0f;
    if (version !== 7) {
      return null;
    }

    // Extract first 48 bits (12 hex chars = 6 bytes) as Unix timestamp in milliseconds
    const timestampHex = hex.substring(0, 12);
    const unixMillis = parseInt(timestampHex, 16);

    // Validate timestamp is reasonable (not in distant past/future)
    const minTimestamp = new Date('2020-01-01').getTime();
    const maxTimestamp = new Date('2100-01-01').getTime();
    if (unixMillis < minTimestamp || unixMillis > maxTimestamp) {
      return null;
    }

    return new Date(unixMillis);
  } catch (error) {
    logger.error('[UUIDv7] Error extracting timestamp:', error);
    return null;
  }
}

/**
 * Check if a UUID string is version 7
 *
 * @param uuidStr - UUID string (with or without hyphens)
 * @returns true if UUID v7, false otherwise
 */
export function isUUIDv7(uuidStr: string): boolean {
  try {
    const hex = uuidStr.replace(/-/g, '');
    if (hex.length !== 32) {
      return false;
    }

    const versionByte = parseInt(hex.substring(12, 14), 16);
    const version = (versionByte >> 4) & 0x0f;
    return version === 7;
  } catch {
    return false;
  }
}
